import { useState, useEffect, useCallback, useRef } from 'react';
import { ALERT_SOUND_URL, LS_MUTE_KEY } from '../constants';

const TOAST_DUR = { TRADE: 30000, OBSERVE: 20000 };

// Alerts must ONLY fire for freshly-emitted signals. A stale browser bundle, a
// feed reconnect, a backfill, or a re-poll can surface an OLD decision that the
// dedup treats as "new" — firing a toast + alarm for a signal that already
// passed (or was later removed from the logs). A trader can act on that and then
// find nothing in the log. So drop anything not emitted in the last few minutes
// (small negative tolerance for client clock skew). A row with no usable
// timestamp never alerts.
const ALERT_FRESH_MS = 10 * 60 * 1000;   // 10 minutes
const ALERT_SKEW_MS  = 2  * 60 * 1000;   // tolerate slightly-future timestamps
function isFreshAlert(d) {
  const t = Date.parse((d && (d.signal_ts || d.bar_ts || d.bar_time)) || '');
  if (!Number.isFinite(t)) return false;
  const age = Date.now() - t;
  return age <= ALERT_FRESH_MS && age >= -ALERT_SKEW_MS;
}

export default function useAlert() {
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(LS_MUTE_KEY) === '1'; }
    catch { return false; }
  });
  const [toasts, setToasts]   = useState([]);   // [{ id, decision, dur }]
  const audioRef              = useRef(null);
  const toastTimersRef        = useRef({});      // id → setTimeout handle
  const alertStopTimerRef     = useRef(null);

  useEffect(() => {
    const a = new Audio(ALERT_SOUND_URL);
    a.preload = 'auto';
    a.volume  = 0.7;
    a.loop    = false;

    // Diagnostics — without these, file load failures vanish silently.
    // Errors here are the most common reason traders report "no sound".
    const onError = (e) => {
      const code = a.error?.code;
      const msg  = ['', 'ABORTED', 'NETWORK', 'DECODE', 'SRC_NOT_SUPPORTED'][code] || `code=${code}`;
      // eslint-disable-next-line no-console
      console.warn(`[useAlert] audio load failed: ${msg} (url=${ALERT_SOUND_URL})`, e);
    };
    const onCanPlay = () => {
      // eslint-disable-next-line no-console
      console.info(`[useAlert] audio ready (url=${ALERT_SOUND_URL})`);
    };
    a.addEventListener('error', onError);
    a.addEventListener('canplaythrough', onCanPlay, { once: true });

    audioRef.current = a;
    return () => {
      a.removeEventListener('error', onError);
      a.removeEventListener('canplaythrough', onCanPlay);
      try { a.pause(); } catch {}
      audioRef.current = null;
      Object.values(toastTimersRef.current).forEach(clearTimeout);
      if (alertStopTimerRef.current) clearTimeout(alertStopTimerRef.current);
    };
  }, []);

  // Unlock audio on a user gesture — required by every modern browser's
  // autoplay policy. The previous implementation removed listeners *before*
  // confirming play() resolved, so a single failed attempt left audio locked
  // forever. This version keeps listeners attached until we get a confirmed
  // successful play (.then), and only then tears them down.
  useEffect(() => {
    let unlocked = false;
    const evts = ['click', 'keydown', 'touchend', 'pointerup'];

    const teardown = () => evts.forEach(ev =>
      document.removeEventListener(ev, unlock, true));

    const unlock = () => {
      if (unlocked) return;
      const a = audioRef.current;
      if (!a) return;
      const vol = a.volume;
      a.muted = true;
      const p = a.play();
      if (!p || typeof p.then !== 'function') {
        // Older browsers: synchronous play, assume unlocked.
        a.pause(); a.currentTime = 0; a.muted = false; a.volume = vol;
        unlocked = true; teardown();
        return;
      }
      p.then(() => {
        a.pause(); a.currentTime = 0; a.muted = false; a.volume = vol;
        unlocked = true; teardown();
      }).catch((err) => {
        a.muted = false; a.volume = vol;
        // eslint-disable-next-line no-console
        console.warn('[useAlert] unlock attempt failed; will retry on next gesture', err);
      });
    };

    evts.forEach(ev => document.addEventListener(ev, unlock, true));
    return teardown;
  }, []);

  useEffect(() => {
    try { localStorage.setItem(LS_MUTE_KEY, muted ? '1' : '0'); } catch {}
  }, [muted]);

  const stopAudio = useCallback(() => {
    if (alertStopTimerRef.current) { clearTimeout(alertStopTimerRef.current); alertStopTimerRef.current = null; }
    const a = audioRef.current;
    if (a) { try { a.loop = false; a.pause(); } catch {} }
  }, []);

  // Restart-safe play: pause → reset → play works even when already looping.
  // Retries once on AbortError (can happen if a previous play() was interrupted).
  // Logs failures to console — silent .catch was the original "no sound" trap.
  const playAlertSound = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.pause();
      a.currentTime = 0;
      a.loop = true;
      const p = a.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[useAlert] play() rejected, retrying once', err?.name || err);
          setTimeout(() => {
            try {
              a.play().catch((e2) => {
                // eslint-disable-next-line no-console
                console.warn('[useAlert] retry also failed (autoplay blocked? bad src?)', e2?.name || e2);
              });
            } catch (e3) {
              // eslint-disable-next-line no-console
              console.warn('[useAlert] retry threw', e3);
            }
          }, 50);
        });
      }
      if (alertStopTimerRef.current) clearTimeout(alertStopTimerRef.current);
      alertStopTimerRef.current = setTimeout(() => {
        if (audioRef.current) { audioRef.current.loop = false; audioRef.current.pause(); }
      }, 120000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[useAlert] playAlertSound threw', err);
    }
  }, []);

  const dismissToast = useCallback((id) => {
    if (toastTimersRef.current[id]) {
      clearTimeout(toastTimersRef.current[id]);
      delete toastTimersRef.current[id];
    }
    setToasts(prev => prev.filter(t => t.id !== id));
    stopAudio();
  }, [stopAudio]);

  const dismissAllToasts = useCallback(() => {
    Object.values(toastTimersRef.current).forEach(clearTimeout);
    toastTimersRef.current = {};
    setToasts([]);
    stopAudio();
  }, [stopAudio]);

  // fireAll: one toast per decision in the batch, each independently auto-dismissed
  const fireAll = useCallback((rawBatch) => {
    // Recency gate — never alert on stale/replayed signals (see isFreshAlert).
    // This is the single choke point for every toast + the alarm sound, so it
    // covers decision toasts, TIER1 trail alerts, and any future source.
    const batch = (rawBatch || []).filter(isFreshAlert);
    if (!batch.length) return;

    // id includes `action` so a single decision row that transitions from
    // TRADE → EXIT renders as two distinct toasts (mirrors App.js's seen-key).
    const newEntries = batch.map(d => ({
      id:       `${d.system_id}-${d.signal_ts}-${d.action}`,
      decision: d,
      dur:      TOAST_DUR[d.action] ?? 12000,
    }));

    setToasts(prev => {
      const existingIds = new Set(prev.map(t => t.id));
      const fresh = newEntries.filter(e => !existingIds.has(e.id));
      return [...fresh, ...prev].slice(0, 10); // newest first, cap at 10
    });

    newEntries.forEach(({ id, dur }) => {
      if (toastTimersRef.current[id]) clearTimeout(toastTimersRef.current[id]);
      toastTimersRef.current[id] = setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
        delete toastTimersRef.current[id];
      }, dur);
    });

    // Sound: restart cleanly on every batch that contains a TRADE.
    // Match the dedup key in App.js (`action_label || action`) so a row whose
    // backend emits `action: <strategy-native>` but `action_label: 'TRADE'`
    // (e.g. TIER1, or any future strategy that doesn't reuse `action` for the
    // canonical verb) still fires the alarm, not just the toast.
    const hasNewTrade = batch.some(d => (d.action_label || d.action) === 'TRADE');
    if (hasNewTrade && !muted) playAlertSound();
  }, [muted, playAlertSound]);

  const toggleMute = useCallback(() => {
    setMuted(m => {
      const next = !m;
      if (next) stopAudio(); // muting during a ringing alert should silence it
      return next;
    });
  }, [stopAudio]);

  const testSound = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.currentTime = 0;
      a.volume = 0.7;
      a.muted = false;
      const p = a.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[useAlert] test play() rejected', err?.name || err,
            '— common causes: file 404 (check ALERT_SOUND_URL), autoplay block (click again), or unsupported codec');
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[useAlert] testSound threw', err);
    }
  }, []);

  return { toasts, fireAll, dismissToast, dismissAllToasts, muted, toggleMute, testSound };
}
